import { Directive, ElementRef, EventEmitter, Input, Output, Renderer2, inject, OnChanges, SimpleChanges, OnInit, OnDestroy } from '@angular/core';
import { GeometryHelper, Rectangle, TransformHelper, MatrixHelper } from '@newroom-connect/library/helpers';
import { BoundingBox } from '@newroom-connect/library/types';

export interface CornerPosition {
  id: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight'; // Which corner is being dragged
  x: number; // Screen coordinates where drag started
  y: number; // Screen coordinates where drag started
}

export interface ResizeEvent {
  width: number;   // Percentage values for resize mode
  height: number;
  top: number;
  left: number;
}

export interface TransformEvent {
  corners: {
    topLeft: { x: number; y: number };     // Screen coordinates
    topRight: { x: number; y: number };
    bottomLeft: { x: number; y: number };
    bottomRight: { x: number; y: number };
  };
}

export enum ResizeMode {
  TRANSFORM = 'TRANSFORM',
  RESIZE = 'RESIZE'
}

@Directive({
  selector: '[nrcResize]',
  standalone: true
})
export class ResizeDirective implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) public targetContainerRef!: ElementRef<HTMLElement>;
  @Input() public mode: 'resize' | 'transform' = 'resize';
  @Input() public activeCorner?: CornerPosition | null; // Reactive input - when set, starts drag
  @Input() public currentCorners?: TransformEvent['corners']; // Current transformed corners for baseline
  @Input() public aspectLocked = false;
  @Input() public aspectRatio = 1;
  @Input() public isResizeDisabled = false;
  // Optional element that receives the matrix3d transform in transform mode
  @Input() public contentElement?: HTMLElement | null;
  // Enable internal resize observer to scale transform corners when container resizes
  @Input() public enableResizeObserver = true;
  // BoundingBox input for external layout updates
  @Input() public boundingBox?: BoundingBox;

  // Backward compatibility
  @Input() public set keepAspectRatio(value: boolean | undefined) {
    if (value !== undefined) {
      this.aspectLocked = value;
    }
  }

  @Output() public resizeEvent = new EventEmitter<ResizeEvent>();
  @Output() public transformEvent = new EventEmitter<TransformEvent>();
  // Emits when a matrix transform is applied (useful to persist transformMatrix)
  @Output() public transformMatrixChanged = new EventEmitter<string>();
  // Emits when corners were scaled due to container resize
  @Output() public cornersScaled = new EventEmitter<TransformEvent['corners']>();

  @Output() public dragStarted = new EventEmitter<CornerPosition>(); // Emit which corner started
  @Output() public dragEnded = new EventEmitter<CornerPosition>(); // Emit which corner ended

  // Drag state
  private isDragging = false;
  private currentActiveCorner?: CornerPosition;
  private startX = 0;
  private startY = 0;

  // Initial state for resize mode
  private initialWidth = 0;
  private initialHeight = 0;
  private initialTop = 0;
  private initialLeft = 0;

  // Initial state for transform mode
  private initialCorners?: TransformEvent['corners'];

  private readonly elementRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);

  // Bound methods for event listeners
  private boundOnDragMove = this.onDragMove.bind(this);
  private boundOnDragEnd = this.onDragEnd.bind(this);
  private resizeObserver?: ResizeObserver;
  private previousContainerRect?: { left: number; top: number; width: number; height: number };
  private lastAppliedCorners?: TransformEvent['corners'];

  /**
   *
   */
  public ngOnInit(): void {
    this.setupResizeObserver();
    // Attempt initial apply after view settles
    setTimeout(() => {
      if (this.mode === 'transform') {
        this.tryApplyInitialMatrix();
      }
    }, 0);
  }

  /**
   * React to input changes, particularly activeCorner changes.
   *
   * @param changes - Angular SimpleChanges object.
   */
  public ngOnChanges(changes: SimpleChanges): void {
    // Handle external bounding box changes
    if (changes['boundingBox'] && !changes['boundingBox'].firstChange && !this.isDragging) {
      this.handleExternalBoundingBoxChange();
    }

    // If contentElement is provided later (after template children initialize), try applying the saved matrix
    if (changes['contentElement'] && this.mode === 'transform' && this.contentElement && this.currentCorners) {
      this.tryApplyInitialMatrix();
    }

    // React to currentCorners changes by applying matrix in transform mode
    if (changes['currentCorners'] && this.mode === 'transform' && this.contentElement && this.currentCorners) {
      this.tryApplyInitialMatrix();
    }

    if (changes['mode'] && this.mode === 'transform') {
      // When switching to transform mode, attempt to apply matrix from current corners
      if (this.contentElement && this.currentCorners) {
        this.tryApplyInitialMatrix();
      }
    }

    if (changes['activeCorner']) {
      const activeCorner = changes['activeCorner'].currentValue;

      if (activeCorner && !this.isDragging) {
        // Start drag when activeCorner is set with corner info
        this.startDrag(activeCorner);
      } else if (!activeCorner && this.isDragging) {
        // End drag when activeCorner is cleared
        this.endDrag();
      }
    }
  }

  public ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
  }

  /**
   * Handle external bounding box changes by updating element layout silently.
   * This method only applies layout changes without emitting any events.
   */
  private handleExternalBoundingBoxChange(): void {
    if (!this.boundingBox || !this.targetContainerRef?.nativeElement) {
      return;
    }

    // Apply layout from bounding box silently
    this.applyLayoutFromBoundingBox();

    // Use setTimeout to ensure DOM updates are applied before synchronizing
    setTimeout(() => {
      if (this.mode === 'transform') {
        // Apply transform silently without emitting events
        if (this.boundingBox?.perspective && this.contentElement) {
          if (TransformHelper.hasPerspectiveData(this.boundingBox)) {
            this.restoreTransformFromBoundingBox();
          } else if (this.currentCorners) {
            this.tryApplyInitialMatrix();
          }
        }
      } else {
        // In resize mode, update container rect tracking
        this.updateContainerRectTracking();
      }
    }, 0);
  }

  /**
   * Apply element layout from bounding box data.
   */
  private applyLayoutFromBoundingBox(): void {
    if (!this.boundingBox) {
      return;
    }

    const element = this.elementRef.nativeElement;

    // Apply percentage-based layout
    this.renderer.setStyle(element, 'width', `${this.boundingBox.width}%`);
    this.renderer.setStyle(element, 'height', `${this.boundingBox.height}%`);
    this.renderer.setStyle(element, 'top', `${this.boundingBox.top}%`);
    this.renderer.setStyle(element, 'left', `${this.boundingBox.left}%`);
  }

  /**
   * Synchronize transform mode with bounding box perspective data.
   */
  private syncTransformModeWithBoundingBox(): void {
    if (!this.boundingBox?.perspective || !this.contentElement) {
      return;
    }

    // If bounding box has perspective data, restore transform from it
    if (TransformHelper.hasPerspectiveData(this.boundingBox)) {
      this.restoreTransformFromBoundingBox();
    } else if (this.currentCorners) {
      // Otherwise, reapply current corners after layout change
      this.tryApplyInitialMatrix();
    }
  }

  /**
   * Restore transform from bounding box perspective data.
   */
  private restoreTransformFromBoundingBox(): void {
    if (!this.boundingBox?.perspective?.cornerPositions) {
      return;
    }

    const elementRect = this.elementRef.nativeElement.getBoundingClientRect();
    const parentRect = this.targetContainerRef.nativeElement.getBoundingClientRect();

    // Use TransformHelper to restore corners from perspective data
    const cornerPositions = TransformHelper.restoreCornersFromPerspective(
      this.boundingBox,
      elementRect,
      parentRect
    );

    // Convert to TransformEvent corners format
    const corners: TransformEvent['corners'] = {
      topLeft: cornerPositions.topLeft,
      topRight: cornerPositions.topRight,
      bottomLeft: cornerPositions.bottomLeft,
      bottomRight: cornerPositions.bottomRight
    };

    // Apply the matrix transform
    this.applyMatrixFromCorners(corners);

    // Emit the restored corners so the component can update its corner handles
    // This is needed for external bounding box updates with perspective data
    this.cornersScaled.emit(corners);
  }

  /**
   * Update container rect tracking for resize observer.
   */
  private updateContainerRectTracking(): void {
    if (!this.targetContainerRef?.nativeElement) {
      return;
    }

    const containerRect = this.targetContainerRef.nativeElement.getBoundingClientRect();

    this.previousContainerRect = {
      left: containerRect.left,
      top: containerRect.top,
      width: containerRect.width,
      height: containerRect.height
    };
  }

  /**
   * Start drag operation when activeCorner input is set.
   *
   * @param corner - The corner being dragged with complete information.
   */
  private startDrag(corner: CornerPosition): void {
    if (this.isResizeDisabled) {
      return;
    }

    this.currentActiveCorner = corner;
    this.isDragging = true;
    this.startX = corner.x; // Use the corner's start position
    this.startY = corner.y;

    if (this.mode === 'resize') {
      this.initializeResizeState();
    } else {
      this.initializeTransformState();
    }

    this.renderer.setStyle(this.elementRef.nativeElement, 'user-select', 'none');

    // Setup global event listeners
    document.addEventListener('mousemove', this.boundOnDragMove);
    document.addEventListener('mouseup', this.boundOnDragEnd);
    document.addEventListener('pointermove', this.boundOnDragMove);
    document.addEventListener('pointerup', this.boundOnDragEnd);

    // Emit which corner started dragging
    this.dragStarted.emit(corner);
  }

  /**
   * Initialize resize mode state.
   */
  private initializeResizeState(): void {
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    const containerWidth = this.targetContainerRef.nativeElement.offsetWidth;
    const containerHeight = this.targetContainerRef.nativeElement.offsetHeight;

    this.initialWidth = 100 * (rect.width / containerWidth);
    this.initialHeight = 100 * (rect.height / containerHeight);
    this.initialTop = 100 * (this.elementRef.nativeElement.offsetTop / containerHeight);
    this.initialLeft = 100 * (this.elementRef.nativeElement.offsetLeft / containerWidth);
  }

  /**
   * Initialize transform mode state.
   */
  private initializeTransformState(): void {
    // Use current transformed corners if available, otherwise fall back to rectangular corners
    if (this.currentCorners) {
      this.initialCorners = { ...this.currentCorners };
    } else {
      const rect = this.elementRef.nativeElement.getBoundingClientRect();

      // Initialize with current rectangular corners as fallback
      this.initialCorners = {
        topLeft: { x: rect.left, y: rect.top },
        topRight: { x: rect.right, y: rect.top },
        bottomLeft: { x: rect.left, y: rect.bottom },
        bottomRight: { x: rect.right, y: rect.bottom }
      };
    }
  }

  /**
   * Apply a perspective matrix to the content element from the provided corners.
   * @param corners
   */
  private applyMatrixFromCorners(corners: TransformEvent['corners']): void {
    if (!this.contentElement) {
      return;
    }

    const rect = this.elementRef.nativeElement.getBoundingClientRect();

    // Guard: wait for layout to produce non-zero size
    if (!rect || rect.width <= 0 || rect.height <= 0) {
      return;
    }

    const transformObject = {
      element: this.contentElement,
      style: '',
      computedStyle: getComputedStyle(this.contentElement),
      width: rect.width,
      height: rect.height,
      useBackfacing: false,
      topLeft: { x: corners.topLeft.x - rect.left, y: corners.topLeft.y - rect.top },
      topRight: { x: corners.topRight.x - rect.left, y: corners.topRight.y - rect.top },
      bottomLeft: { x: corners.bottomLeft.x - rect.left, y: corners.bottomLeft.y - rect.top },
      bottomRight: { x: corners.bottomRight.x - rect.left, y: corners.bottomRight.y - rect.top }
    };

    if (MatrixHelper.hasTransformError(transformObject)) {
      return;
    }

    const result = MatrixHelper.transform(transformObject);

    this.contentElement.style.transform = result.style;
    this.transformMatrixChanged.emit(result.style);
    this.lastAppliedCorners = { ...corners };
  }

  /**
   * Attempt to apply the saved matrix once layout dimensions are ready.
   */
  private tryApplyInitialMatrix(): void {
    if (!this.contentElement || !this.currentCorners) {
      return;
    }

    const rect = this.elementRef.nativeElement.getBoundingClientRect();

    if (!rect || rect.width <= 0 || rect.height <= 0) {
      // Retry on next animation frame if element not laid out yet
      requestAnimationFrame(() => this.tryApplyInitialMatrix());
      return;
    }

    this.applyMatrixFromCorners(this.currentCorners);
  }

  /**
   * Handle drag move events.
   *
   * @param event - Mouse or pointer event.
   */
  private onDragMove(event: MouseEvent | PointerEvent): void {
    if (!this.isDragging || !this.currentActiveCorner) {
      return;
    }

    event.preventDefault();

    const deltaX = event.clientX - this.startX;
    const deltaY = event.clientY - this.startY;

    if (this.mode === 'resize') {
      this.handleResizeMode(deltaX, deltaY);
    } else {
      this.handleTransformMode(deltaX, deltaY);
    }
  }

  /**
   * Handle resize mode drag - uses corner.id to determine resize behavior.
   *
   * @param deltaX - X delta from start position.
   * @param deltaY - Y delta from start position.
   */
  private handleResizeMode(deltaX: number, deltaY: number): void {
    if (!this.currentActiveCorner) {
      return;
    }

    const containerWidth = this.targetContainerRef.nativeElement.offsetWidth;
    const containerHeight = this.targetContainerRef.nativeElement.offsetHeight;

    // Convert initial percentages to pixels for calculation
    const initialRect: Rectangle = {
      left: (this.initialLeft / 100) * containerWidth,
      top: (this.initialTop / 100) * containerHeight,
      width: (this.initialWidth / 100) * containerWidth,
      height: (this.initialHeight / 100) * containerHeight
    };

    // Use GeometryHelper for resize calculations
    const newRect = GeometryHelper.calculateResizeDimensions(
      initialRect,
      this.currentActiveCorner.id,
      deltaX,
      deltaY,
      this.aspectLocked ? this.aspectRatio : undefined
    );

    // Convert back to percentages
    const newWidth = (newRect.width / containerWidth) * 100;
    const newHeight = (newRect.height / containerHeight) * 100;
    const newTop = (newRect.top / containerHeight) * 100;
    const newLeft = (newRect.left / containerWidth) * 100;

    // Update element styles
    this.renderer.setStyle(this.elementRef.nativeElement, 'width', `${newWidth}%`);
    this.renderer.setStyle(this.elementRef.nativeElement, 'height', `${newHeight}%`);
    this.renderer.setStyle(this.elementRef.nativeElement, 'top', `${newTop}%`);
    this.renderer.setStyle(this.elementRef.nativeElement, 'left', `${newLeft}%`);

    // Emit resize event
    this.resizeEvent.emit({
      width: newWidth,
      height: newHeight,
      top: newTop,
      left: newLeft
    });
  }

  /**
   * Handle transform mode drag - uses corner.id to update specific corner.
   *
   * @param deltaX - X delta from start position.
   * @param deltaY - Y delta from start position.
   */
  private handleTransformMode(deltaX: number, deltaY: number): void {
    if (!this.initialCorners || !this.currentActiveCorner) {
      return;
    }

    // Update only the active corner using its ID, keep others unchanged
    const updatedCorners = { ...this.initialCorners };
    const originalCorner = this.initialCorners[this.currentActiveCorner.id];

    updatedCorners[this.currentActiveCorner.id] = {
      x: originalCorner.x + deltaX,
      y: originalCorner.y + deltaY
    };

    // Emit transform event with updated corners
    this.transformEvent.emit({ corners: updatedCorners });

    // If we have a content element, apply matrix immediately for live feedback
    if (this.mode === 'transform' && this.contentElement) {
      this.applyMatrixFromCorners(updatedCorners);
    }
  }

  /**
   * Handle drag end.
   */
  private onDragEnd(): void {
    this.endDrag();
  }

  /**
   * End drag operation.
   */
  private endDrag(): void {
    if (!this.isDragging || !this.currentActiveCorner) {
      return;
    }

    const draggedCorner = this.currentActiveCorner;

    this.isDragging = false;
    this.currentActiveCorner = undefined;
    this.initialCorners = undefined;

    this.renderer.setStyle(this.elementRef.nativeElement, 'user-select', 'auto');

    // Remove global event listeners
    document.removeEventListener('mousemove', this.boundOnDragMove);
    document.removeEventListener('mouseup', this.boundOnDragEnd);
    document.removeEventListener('pointermove', this.boundOnDragMove);
    document.removeEventListener('pointerup', this.boundOnDragEnd);

    // Emit which corner finished dragging
    this.dragEnded.emit(draggedCorner);
  }

  /**
   * Setup a resize observer to scale transform corners when container resizes.
   */
  private setupResizeObserver(): void {
    if (!this.enableResizeObserver || !this.targetContainerRef?.nativeElement) {
      return;
    }

    const containerRect = this.targetContainerRef.nativeElement.getBoundingClientRect();

    this.previousContainerRect = {
      left: containerRect.left,
      top: containerRect.top,
      width: containerRect.width,
      height: containerRect.height
    };

    this.resizeObserver = new ResizeObserver(() => {
      this.handleContainerResize();
    });

    this.resizeObserver.observe(this.targetContainerRef.nativeElement);
  }

  /**
   * Handle container resize: scale the existing transform corners proportionally.
   */
  private handleContainerResize(): void {
    if (this.mode !== 'transform' || !this.currentCorners || !this.previousContainerRect) {
      // No transform scaling required for resize mode or uninitialized state
      // Still update previous rect for future calculations
      const rect = this.targetContainerRef.nativeElement.getBoundingClientRect();

      this.previousContainerRect = { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
      return;
    }

    const currentContainerRect = this.targetContainerRef.nativeElement.getBoundingClientRect();

    // Convert current corners to CornerPositions object
    const currentCornerPositions = {
      topLeft: { x: this.currentCorners.topLeft.x, y: this.currentCorners.topLeft.y },
      topRight: { x: this.currentCorners.topRight.x, y: this.currentCorners.topRight.y },
      bottomLeft: { x: this.currentCorners.bottomLeft.x, y: this.currentCorners.bottomLeft.y },
      bottomRight: { x: this.currentCorners.bottomRight.x, y: this.currentCorners.bottomRight.y }
    };

    // Map to parent percentages with previous rect then back to current rect
    const percentageCorners = TransformHelper.cornersToParentPercentages(
      currentCornerPositions,
      this.previousContainerRect
    );

    const newCornerPositions = TransformHelper.parentPercentagesToCorners(
      percentageCorners,
      {
        left: currentContainerRect.left,
        top: currentContainerRect.top,
        width: currentContainerRect.width,
        height: currentContainerRect.height
      }
    );

    // Update previous rect snapshot
    this.previousContainerRect = {
      left: currentContainerRect.left,
      top: currentContainerRect.top,
      width: currentContainerRect.width,
      height: currentContainerRect.height
    };

    // Emit scaled corners and apply matrix if applicable
    const scaledCorners: TransformEvent['corners'] = {
      topLeft: newCornerPositions.topLeft,
      topRight: newCornerPositions.topRight,
      bottomLeft: newCornerPositions.bottomLeft,
      bottomRight: newCornerPositions.bottomRight
    };

    this.cornersScaled.emit(scaledCorners);

    if (this.contentElement) {
      this.applyMatrixFromCorners(scaledCorners);
    }
  }
}
